/**
 * AssignBranchModal — Phase R10.4c (2026-07-05)
 *
 * Reused for BOTH:
 *   - Creating a new mapping from the Unmapped Outlets tab (mode="create")
 *   - Editing an existing mapping from the Saved Mappings tab (mode="edit")
 *
 * Props:
 *   mode              — "create" | "edit"
 *   initialOutlet     — string (original detected outlet; locked in create mode)
 *   initialBranchId   — string | null
 *   normalizedOutlet  — string (shown as muted hint)
 *   receiptCount      — number (shown in create-mode as "Apply to N" hint)
 *   branches          — [{id, name}, …]
 *   loadingBranches   — bool
 *   pending           — bool, submit in-flight
 *   onSubmit({ mappedBranchId, applyToPending })
 *   onClose()
 */
import { useMemo, useState } from 'react';
import { X, Loader2, MapPin } from 'lucide-react';

export default function AssignBranchModal({
  mode = 'create',
  initialOutlet = '',
  initialBranchId = null,
  normalizedOutlet = '',
  receiptCount = 0,
  branches = [],
  loadingBranches = false,
  pending = false,
  onSubmit,
  onClose,
}) {
  const [branchId, setBranchId]       = useState(initialBranchId ?? '');
  const [applyPending, setApplyPending] = useState(true);
  const title = mode === 'edit'
    ? 'Edit Outlet Mapping'
    : 'Save Outlet Mapping';
  const submitLabel = mode === 'edit' ? 'Save Changes' : 'Save Mapping';
  const canSubmit = !pending && !!branchId;

  const selectedBranchName = useMemo(() => {
    const match = branches.find((b) => b.id === branchId);
    return match?.name ?? null;
  }, [branches, branchId]);

  const close = () => { if (!pending) onClose?.(); };

  const submit = () => {
    if (!canSubmit) return;
    onSubmit?.({
      mappedBranchId: branchId,
      applyToPending: mode === 'create' ? applyPending : false,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      data-testid="outlet-mapping-modal"
      onClick={close}
    >
      <div
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#15151B] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gold mb-1">
              Outlet Mapping
            </div>
            <h2 className="text-xl font-bold text-white">{title}</h2>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={pending}
            data-testid="outlet-mapping-modal-close"
            className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/5 disabled:opacity-40"
          >
            <X size={16} />
          </button>
        </div>

        <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-3 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-1">
            Detected Outlet
          </div>
          <div className="text-sm text-white font-semibold" data-testid="outlet-mapping-modal-outlet">
            {initialOutlet || '—'}
          </div>
          {normalizedOutlet ? (
            <div className="text-xs text-white/50 font-mono mt-0.5">
              normalized: {normalizedOutlet}
            </div>
          ) : null}
        </div>

        <label className="block mb-2 text-xs font-semibold text-white/70">
          Mapped Branch <span className="text-red-300">*</span>
        </label>
        <select
          value={branchId}
          onChange={(e) => setBranchId(e.target.value)}
          disabled={loadingBranches || pending}
          data-testid="outlet-mapping-modal-branch-select"
          className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none"
        >
          <option value="">
            {loadingBranches ? 'Loading branches…' : 'Select a branch…'}
          </option>
          {branches.map((b) => (
            <option key={b.id} value={b.id} data-testid={`outlet-mapping-modal-branch-option-${b.id}`}>
              {b.name}
            </option>
          ))}
        </select>

        {mode === 'create' && receiptCount > 0 ? (
          <label className="flex items-center gap-2 mt-4 text-xs text-white/75">
            <input
              type="checkbox"
              checked={applyPending}
              onChange={(e) => setApplyPending(e.target.checked)}
              disabled={pending}
              data-testid="outlet-mapping-modal-apply-pending"
              className="rounded border-white/20 bg-white/10 text-gold focus:ring-gold"
            />
            Apply to {receiptCount} pending receipt{receiptCount === 1 ? '' : 's'}
          </label>
        ) : null}

        {mode === 'edit' ? (
          <div className="mt-4 text-xs text-white/50">
            Historical claims already tagged by this mapping will stay
            assigned to their previous branch — only new auto-resolutions
            use the updated target.
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-5">
          <button
            type="button"
            onClick={close}
            disabled={pending}
            data-testid="outlet-mapping-modal-cancel"
            className="px-4 py-2 text-sm text-white/70 hover:text-white disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="outlet-mapping-modal-submit"
            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-semibold rounded-xl px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {pending ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
            {submitLabel}
            {selectedBranchName ? (
              <span className="text-black/70 font-normal">· {selectedBranchName}</span>
            ) : null}
          </button>
        </div>
      </div>
    </div>
  );
}
